/**
 * Tasks API · core.tasks
 *
 * RLS hace el filtering. El cliente nunca decide a quién mostrar — solo
 * pide y la DB devuelve lo que corresponde.
 */
import { supabase } from '@/lib/supabase';
import type {
  TaskScope,
  TaskStatus,
  TasksInsert,
  TasksRow,
  TasksUpdate,
} from '@/types/database.types';

export interface ListTasksFilters {
  areaId?: string;
  status?: TaskStatus[];
  prospectId?: string;
  /**
   * Filtra por proyecto. Usado por el workspace de proyecto (Anexo B Fase W1)
   * para mostrar solo las tareas vinculadas a un project_id puntual.
   */
  projectId?: string;
  /**
   * Filtra por eje team/personal. Si no se pasa, devuelve ambos.
   * RLS sigue mandando: el cliente NUNCA decide visibilidad.
   */
  scope?: TaskScope;
  /**
   * Si true, incluye archived. Por default los listados los ocultan.
   */
  includeArchived?: boolean;
  /**
   * Si true, incluye subtareas (tasks con parent_task_id IS NOT NULL).
   * Default false → el board principal NO muestra subtareas mezcladas con
   * sus padres. Se acceden desde el drawer del padre (sección 02 Subtareas).
   */
  includeSubtasks?: boolean;
}

export async function listTasks(filters: ListTasksFilters = {}): Promise<TasksRow[]> {
  let q = supabase
    .from('tasks')
    .select('*')
    .is('deleted_at', null)
    .order('position', { ascending: true })
    .order('created_at', { ascending: false });

  if (filters.areaId) q = q.eq('area_id', filters.areaId);
  if (filters.prospectId) q = q.eq('prospect_id', filters.prospectId);
  if (filters.projectId) q = q.eq('project_id', filters.projectId);
  if (filters.scope) q = q.eq('scope', filters.scope);
  if (filters.status && filters.status.length > 0) {
    q = q.in('status', filters.status);
  } else if (!filters.includeArchived) {
    q = q.neq('status', 'archived');
  }
  // Por default OCULTAR subtareas del listado plano. Se ven sólo cuando se
  // abre el drawer del padre (sección 02), o si el caller pide includeSubtasks=true.
  if (!filters.includeSubtasks) {
    q = q.is('parent_task_id', null);
  }

  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

export async function getTask(id: string): Promise<TasksRow | null> {
  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

/**
 * Cadena de pushes EN VUELO por task_id. Serializa los sync de una misma tarea
 * para evitar la race de doble-creación: al crear una tarea CON asignados se
 * disparan dos pushes muy juntos (createTask + el re-sync post-bulkAssign); si
 * corren concurrentes, ninguno ve el link del otro y ambos crean un evento →
 * duplicado en Google. Encadenándolos, el 2º push ya ve el link del 1º y hace
 * PATCH en vez de POST.
 */
const syncChain = new Map<string, Promise<unknown>>();

/**
 * Sync best-effort de una tarea al calendario destino (Fase B6c · core→Google).
 * Fire-and-forget: NO se espera ni bloquea la mutación, y cualquier error se
 * traga (el edge no-opea si el user no tiene sync configurado). El motor real
 * (anti-eco, scope, conflictos) vive en core-task-sync-push. Serializado por
 * task_id (ver syncChain) para no duplicar eventos.
 */
function fireTaskSync(taskId: string, action: 'upsert' | 'delete'): void {
  const prev = syncChain.get(taskId) ?? Promise.resolve();
  const next = prev
    .catch(() => {})
    .then(() =>
      supabase.functions.invoke('core-task-sync-push', { body: { task_id: taskId, action } }),
    )
    .catch(() => {
      /* sync best-effort · no romper el flujo de tareas */
    });
  syncChain.set(taskId, next);
  void next.finally(() => {
    if (syncChain.get(taskId) === next) syncChain.delete(taskId);
  });
}

/**
 * Re-proyecta una tarea al calendario tras un cambio que NO pasa por update()
 * de la fila (ej. sumar/quitar asignados → recalcula attendees del evento).
 * Fire-and-forget, mismo contrato best-effort que el resto del sync.
 */
export function syncTaskToCalendar(taskId: string): void {
  fireTaskSync(taskId, 'upsert');
}

export interface TaskSyncPullResult {
  imported: number;
  updated: number;
  deleted: number;
  /** Reuniones adoptadas refrescadas desde Google (título/desc/fecha · B6e). */
  refreshed: number;
}

/**
 * Trae del calendario destino los eventos creados fuera de la plataforma y los
 * refleja como tareas (Fase B6d · Google→core). No-opea si el user no tiene sync
 * configurado o si el modo es core_to_google. Nunca lanza: degrada a ceros.
 */
export async function pullTaskSync(): Promise<TaskSyncPullResult> {
  try {
    const { data, error } = await supabase.functions.invoke('core-task-sync-pull', { body: {} });
    if (error) return { imported: 0, updated: 0, deleted: 0, refreshed: 0 };
    const res = data as Partial<TaskSyncPullResult> & { ok?: boolean };
    return {
      imported: res.imported ?? 0,
      updated: res.updated ?? 0,
      deleted: res.deleted ?? 0,
      refreshed: res.refreshed ?? 0,
    };
  } catch {
    return { imported: 0, updated: 0, deleted: 0, refreshed: 0 };
  }
}

export interface TaskSyncBackfillResult {
  created: number;
  updated: number;
  unchanged: number;
  failed: number;
  total: number;
}

/**
 * Backfill core→Google: proyecta TODAS las tareas con fecha del user al
 * calendario destino (crea las que faltan + corrige color/attendees de las ya
 * sincronizadas, saltea las sin cambios). No-opea si el sync no está configurado
 * o el modo es google_to_core. Nunca lanza: degrada a ceros.
 */
export async function backfillTaskSync(): Promise<TaskSyncBackfillResult> {
  const zero: TaskSyncBackfillResult = { created: 0, updated: 0, unchanged: 0, failed: 0, total: 0 };
  try {
    const { data, error } = await supabase.functions.invoke('core-task-sync-backfill', { body: {} });
    if (error) return zero;
    const res = data as Partial<TaskSyncBackfillResult> & { ok?: boolean };
    return {
      created: res.created ?? 0,
      updated: res.updated ?? 0,
      unchanged: res.unchanged ?? 0,
      failed: res.failed ?? 0,
      total: res.total ?? 0,
    };
  } catch {
    return zero;
  }
}

export async function createTask(input: TasksInsert): Promise<TasksRow> {
  const { data, error } = await supabase
    .from('tasks')
    .insert(input)
    .select('*')
    .single();
  if (error) throw error;
  fireTaskSync(data.id, 'upsert');
  return data;
}

export async function updateTask(id: string, patch: TasksUpdate): Promise<TasksRow> {
  const { data, error } = await supabase
    .from('tasks')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  fireTaskSync(id, 'upsert');
  return data;
}

/** Soft delete · marca deleted_at. La fila se filtra con `.is('deleted_at', null)`. */
export async function softDeleteTask(id: string): Promise<void> {
  const { error } = await supabase
    .from('tasks')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
  fireTaskSync(id, 'delete');
}
